import { Component, Inject, ViewChild } from '@angular/core';
import { ToastService } from '@app/core/services/toast/toast.service';
import { IScheduleTemplateMappedItem } from '@app/logic/schedule-templates/interfaces/i.schedule-template.mapped';
import { BaseDialogFormViewDirective } from '@app/shared/base-views/base-dialog-form-view.directive';
import {
    COST_TYPE_ENUM,
    CostTypeEnumId,
    IIdAndLabelDto,
    IProductCentreDocumentDto,
    IProductOfferingDto,
    IScheduleTemplateItemDto,
    ISlotDto,
    LOT_SPEC_TEMPLATE_COST_TYPE_ENUM,
    UNIT_OF_MEASURE_ENUM
} from '@classictechsolutions/hubapi-transpiled-enums';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    IScheduleTemplateItemMappedItem
} from '@app/logic/schedule-template-items/interfaces/i.schedule-template-item.mapped';
import {
    IScheduleTemplateItemsLogicService
} from '@app/logic/schedule-template-items/interfaces/i.schedule-template-items.logic-service';
import { IManageDialogData } from '@app/shared/interfaces/i.manage-dialog-data';
import { toPromisedArray } from 'cb-hub-lib';
import { SpecGroupsLogicService } from '@app/logic/spec-groups';
import { map, take } from 'rxjs';
import { find, forEach, orderBy } from 'lodash';
import { IProductMappedItem, ProductLogicService } from '@app/logic/products';
import { SlotsLogicService } from '@app/logic/slots/slots.logic-service';
import { NgForm, UntypedFormGroup } from '@angular/forms';
import { IExistingSlotItem } from '@app/views/products/tabs/product-centre/product-centre-tab.component';

@Component({
    selector: 'cb-manage-schedule-template-item-dialog',
    templateUrl: './manage-schedule-template-item-dialog.component.html',
    styleUrls: ['./manage-schedule-template-item-dialog.component.scss'],
    standalone: false
})
export class ManageScheduleTemplateItemDialogComponent
    extends BaseDialogFormViewDirective<
    IScheduleTemplateItemDto, IScheduleTemplateItemMappedItem, IScheduleTemplateItemsLogicService> {
    public static readonly MIN_WIDTH = '50%';

    public mappedItem: IScheduleTemplateItemMappedItem;
    public parentMappedItem: IScheduleTemplateMappedItem;
    public uploadingImage = false;
    public houseAreas = toPromisedArray(this.specGroupsLogicService.$getList().pipe(
        map(results => orderBy(results, 'sortOrder', 'asc'))));
    public COST_TYPE_ENUM = COST_TYPE_ENUM;
    public scheduleTemplateCostTypes = LOT_SPEC_TEMPLATE_COST_TYPE_ENUM.toLookup();
    public selectedProduct: IProductMappedItem | string | any;
    public offeringCategoryId: number;
    public productSearchText: string;
    public productUom: string;
    public slot: ISlotDto;
    public categoryLoaded: boolean;
    public suppliers: IIdAndLabelDto[];
    public hasReceivedStartupEmission = false;
    public businessEntityIds: Set<number> = new Set();
    public categoryPathString: string;
    public existingSlotItem: IExistingSlotItem;

    @ViewChild('form') public itemForm: NgForm;

    constructor(
        private readonly toastService: ToastService,
        private readonly slotService: SlotsLogicService,
        private readonly specGroupsLogicService: SpecGroupsLogicService,
        public dialogRef: MatDialogRef<ManageScheduleTemplateItemDialogComponent>,
        public readonly productLogicService: ProductLogicService,
        @Inject(MAT_DIALOG_DATA) public readonly data: IManageDialogData<IScheduleTemplateItemMappedItem> & { parentMappedItem: IScheduleTemplateMappedItem },
    ) {
        super(dialogRef, toastService);
        this.mappedItem = data.mappedItem;
        this.parentMappedItem = data.parentMappedItem;

        this.slotService.$getItem(this.mappedItem.slotId).pipe(take(1)).subscribe(slot => {
            this.slot = slot;
            this.offeringCategoryId = this.slot?.categories[0]?.id;
            this.categoryLoaded = true;
        });

        if (this.mappedItem?.product?.id) {
            this.productLogicService.$getMappedItem(this.mappedItem.product.id).pipe(take(1)).subscribe(product => {
                this.selectedProduct = product;
                this.categoryPathString = this.getCategoryPathFromArray(product.categoryPath);
                this.setSelectedProductImage();
            });
        } else {
            if (this.mappedItem.productOther) {
                this.selectedProduct = this.mappedItem.productOther;
                this.productSearchText = this.mappedItem.productOther;
                this.selectedProduct = { name: this.mappedItem.productOther };
            }
        }
        this.setBusinessEntityIds();
        this.setExistingSlotItem(this.mappedItem);
    }

    private setBusinessEntityIds(): void {
        if(this.parentMappedItem.isNational) {
            this.businessEntityIds.add(0);
        } else {
            this.parentMappedItem.regionIds?.forEach(region => {
                this.businessEntityIds.add(region);
            });
            this.businessEntityIds.add(0);
        }
    }

    private setExistingSlotItem(mappedItem: IScheduleTemplateItemMappedItem): void {
        this.existingSlotItem = {
            id: mappedItem.product?.id ?? 0,
            slotName: mappedItem.slotName,
            itemName: mappedItem.product?.name ?? mappedItem.productOther,
            houseAreaName: mappedItem.specGroupDto?.label
        } as IExistingSlotItem;
    }

    public get shouldHideQuantity(): boolean {
        return this.mappedItem.costType === COST_TYPE_ENUM.Provisional || !this.mappedItem.costType;
    }

    public isSupplierViewable = (): boolean => {
        const currentCostType = find(this.scheduleTemplateCostTypes, { id: this.mappedItem.costType });
        if (!currentCostType || this.parentMappedItem.isNational) {
            return false;
        }
        return (this.mappedItem.costType === COST_TYPE_ENUM.Actual && this.selectedProduct !== undefined);
    };

    public productSearchTextChanged(str: string): void {
        // Stop initial emission of '' from clearing the product
        if (this.hasReceivedStartupEmission) {
            this.productSearchText = str;
            this.mappedItem.product = null;
            this.productUom = null;
        }
        this.hasReceivedStartupEmission = true;
    }

    private getCategoryPathFromArray(categoryPath: any[]): string {
        return categoryPath
            .map(category => (typeof category === 'string' ? category : category.label))
            .join(' > ');
    }

    public productSelectedFromAutoComplete = (data?: IProductCentreDocumentDto | null, productUpdatedFromDialog = false): void => {
        if (data != null) {
            if(productUpdatedFromDialog) {
                this.itemForm.form.controls.selectProduct.markAsDirty();
            }
            if (data.id) {
                this.productLogicService.$getMappedItem(data.id).pipe(take(1)).subscribe(res => {
                    this.selectedProduct = res;
                    this.setSelectedProductImage();
                    this.setProductValues(data);
                });
            } else {
                this.selectedProduct = data;
            }

            if (this.selectedProduct?.images && this.selectedProduct?.images[0]?.id) {
                this.mappedItem.hasProductImage = true;
            } else {
                this.mappedItem.hasProductImage = false;
            }
            this.setProductValues(data);
        }
    };

    public onCostTypeChange(costType: CostTypeEnumId): void {
        this.mappedItem.product = undefined;
        this.selectedProduct = undefined;
        this.productSearchText = undefined;
        if (costType === COST_TYPE_ENUM.Actual) {
            this.mappedItem.productOther = undefined;
        }
        if (costType === COST_TYPE_ENUM.Estimate || costType === COST_TYPE_ENUM.Provisional) {
            this.mappedItem.rateSnapshot = 0;
        }
    }

    private setSelectedProductImage(): void {
        if (this.selectedProduct?.images[0]?.id) {
            this.selectedProduct.baseImageUrl += `${this.selectedProduct.images[0].id}.${this.selectedProduct.images[0].extension}`;
        } else {
            this.selectedProduct.baseImageUrl = undefined;
        }
    }

    public removeImage(form: UntypedFormGroup): void {
        this.mappedItem.$shouldRemoveImageOnSave = true;
        this.mappedItem.scheduleItemImageUrl = undefined;
        form.markAsDirty();
    }

    private setProductValues(data: IProductCentreDocumentDto): void {
        if (this.selectedProduct.images?.length) {
            this.mappedItem.productImageUrl = this.selectedProduct.baseImageUrl;
        }
        this.productSearchText = data.name;
        this.productUom = data.uomDisplay;
        if (this.selectedProduct && this.mappedItem?.product?.id !== this.selectedProduct?.id) {
            this.mappedItem.productOther = null;
            this.categoryPathString = this.getCategoryPathFromArray(this.selectedProduct.categoryPath);
            this.loadProduct();
        }
    }

    public getRateLabel(): string {
        const costType = find(this.scheduleTemplateCostTypes, { id: this.mappedItem.costType });
        if (costType) {
            return costType.label + ' Amount';
        }
        return 'Amount';
    }

    public presave(): void {

        if (this.shouldHideQuantity) {
            // quantity is hidden so default to 1;
            this.mappedItem.quantity = 1;
        }

        if (this.mappedItem.supplierId === 0) {
            delete this.mappedItem.supplierId;
        }
    }

    public save(): void {
        this.saveInProgress = true;
        this.presave();
        this.saveMethod().pipe(take(1)).subscribe({
            next: (result) => {
                this.handleNext(this.handleSaveSuccess(result));
                this.saveInProgress = false;
            },
            error: (error) => {
                this.handleError(this.handleSaveError(error));
                this.saveInProgress = false;
            }
        });
    }

    public selectFile = (newFile: File, form: UntypedFormGroup): void => {
        if (this.uploadingImage) {
            return;
        }
        this.mappedItem.$scheduleItemImageFile = newFile;

        const reader = new FileReader();
        reader.addEventListener(
            'load', () => {
                this.mappedItem.scheduleItemImageUrl = reader.result as any;
                this.mappedItem.$shouldRemoveImageOnSave = false;
                form.markAsDirty();
            },
            false
        );

        if (newFile) {
            reader.readAsDataURL(newFile);
        }
    };

    public loadProduct = (): void => {
        if (!this.selectedProduct) {
            return;
        }

        this.suppliers = [{ id: 0, label: 'Not Specified' }];
        forEach(this.selectedProduct.rates, (rate) => {
            if (rate.businessAccountId && !find(this.suppliers, { id: rate.businessAccountId })) {
                this.suppliers.push({ id: rate.businessAccountId, label: rate.vendor });
            }
        });

        if (!this.mappedItem?.product) {
            this.mappedItem.product = { id: 0, name: '' };
        }
        if (this.selectedProduct.hasProductImage && this.selectedProduct?.images?.length) {
            this.mappedItem.productImageUrl = this.selectedProduct.images[0];
        } else {
            this.mappedItem.productImageUrl = null;
        }
        this.mappedItem.product.id = (this.selectedProduct as IProductOfferingDto)?.id;
        this.mappedItem.product.name = (this.selectedProduct as IProductOfferingDto)?.name;
        this.productUom = UNIT_OF_MEASURE_ENUM[(this.selectedProduct as IProductOfferingDto)?.uom];
    };
}
